/* iter237 — Read-only billing integrity scan. Finds $70k→$0 style anomalies.
   Auto-corrects nothing; every row is for manual review. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { ShieldAlert, RefreshCw, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONF = { high: 'bg-red-500/15 text-red-300 border-red-500/40', medium: 'bg-amber-500/15 text-amber-300 border-amber-500/40', low: 'bg-[#262626] text-[#A3A3A3] border-[#333]' };

const BillingIntegrityTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/billing/diagnostics/integrity`, { withCredentials: true });
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to run scan'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="billing-integrity" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-['Outfit'] text-xl font-semibold text-white flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-red-400" /> Integrity Scan
            <span className="text-[#A3A3A3] text-sm font-normal">· read-only</span>
          </h3>
          <p className="text-[#737373] text-xs mt-1 max-w-3xl">Detects lost amounts, $0 totals with positive lines, office-to-bill with no balance, duplicate invoices per completion, missing intake timestamps and broken links. Nothing is auto-corrected.</p>
        </div>
        <button data-testid="integrity-refresh" onClick={load} className="inline-flex items-center gap-1.5 text-xs text-white bg-[#262626] hover:bg-[#333] rounded px-3 py-1.5"><RefreshCw className="w-3.5 h-3.5" /> Re-run</button>
      </div>

      {data && (
        <div className="grid sm:grid-cols-4 gap-3">
          {[['Total', data.total_findings, 'text-white'], ['High', data.by_confidence.high, 'text-red-400'], ['Medium', data.by_confidence.medium, 'text-amber-400'], ['Low', data.by_confidence.low, 'text-[#A3A3A3]']].map(([l, v, c]) => (
            <div key={l} className="bg-[#141414] border border-[#262626] rounded-md p-4">
              <p className="text-[#525252] text-xs uppercase tracking-wide">{l}</p>
              <p className={`mt-2 text-3xl font-bold ${c}`}>{v}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-[#A3A3A3]">Running scan…</p>
      ) : (data && data.findings.length === 0) ? (
        <p className="text-green-400 bg-[#141414] border border-[#262626] rounded-md p-6 text-center">No integrity anomalies found. All invoices reconcile. ✅</p>
      ) : (
        <div className="space-y-2">
          {data?.findings.map((f, i) => (
            <div key={i} data-testid={`integrity-finding-${i}`} className="bg-[#141414] border border-[#262626] rounded-md p-3">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${CONF[f.confidence] || CONF.low}`}>{f.confidence}</span>
                  <span className="text-white font-medium">{f.issue}</span>
                </div>
                {f.invoice_id && <Link to={`/admin/invoices?status=all&open=${f.invoice_id}`} className="inline-flex items-center gap-1 text-[#D4AF37] hover:text-[#F3E5AB] text-xs font-bold"><ExternalLink className="w-3 h-3" /> Open invoice</Link>}
              </div>
              <div className="mt-2 grid md:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                <p className="text-[#A3A3A3]"><span className="text-[#525252]">Customer:</span> {f.customer_name || '—'}{f.technician_name ? ` · ${f.technician_name}` : ''}</p>
                <p className="text-[#A3A3A3]"><span className="text-[#525252]">Invoice #:</span> {f.invoice_number || '—'}</p>
                <p className="text-[#A3A3A3] break-all"><span className="text-[#525252]">Evidence:</span> {f.evidence}</p>
                <p className="text-[#A3A3A3]"><span className="text-[#525252]">Proposed:</span> <span className="text-white">{f.proposed_correction}</span></p>
                {f.submission_snapshot?.invoice_total != null && (
                  <p className="text-[#A3A3A3]"><span className="text-[#525252]">Original submitted total:</span> <span className="text-green-400">${Number(f.submission_snapshot.invoice_total).toFixed(2)}</span></p>
                )}
                <p className="text-[#525252] break-all">ids: inv {(f.invoice_id || '—')} · comp {(f.completion_id || '—')} · appt {(f.appointment_id || '—')}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BillingIntegrityTab;
